import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { ApiError } from "@njbsoft/api-client";
import { type components } from "@njbsoft/types";
import { apiClient } from "@/lib/auth";
import { deviceInventoryKeys, installationChainKeys } from "./use-device-inventory";

type DeviceInstallation = components["schemas"]["DeviceInstallation"];
type DeviceInstallationListResponse = components["schemas"]["DeviceInstallationListResponse"];
type DeviceInstallationUpdate = components["schemas"]["DeviceInstallationUpdate"];
type DeviceInstallationCreateRequest = components["schemas"]["DeviceInstallationCreateRequest"];
type ComplianceVerdict = components["schemas"]["ComplianceVerdict"];
type DeviceLifecycleStatus = components["schemas"]["DeviceLifecycleStatus"];
type DeviceServiceType = components["schemas"]["DeviceServiceType"];

// Per AWWA M14 §6.5 the installation row is the per-premise installation
// context — its lifecycle changes more frequently than the inventory's
// IUR identity tuple. Shorter staleTime suits in_service/out_of_service
// flips, detach/transfer/replace flows.
const LIST_STALE_TIME = 2 * 60 * 1000;
const LIST_GC_TIME = 10 * 60 * 1000;

export interface DeviceInstallationListParams {
  facility_id?: string;
  device_inventory_id?: string;
  device_category?: "testable_assembly" | "air_gap" | "non_testable_device";
  /** Water-service class the assembly sits on — exact match. */
  service_type?: DeviceServiceType;
  /** Tri-state protective-enclosure filter; omit for every assembly. */
  has_protective_enclosure?: boolean;
  /** Lifecycle set — a row matches when its status is any member. Omit for every status. */
  device_status?: DeviceLifecycleStatus[];
  /** ANDs with `non_compliant_only` rather than replacing it. */
  verdict?: ComplianceVerdict;
  search?: string;
  /** The RESOLVED correspondence account — the premise's flagged,
   * non-archived association. */
  account_id?: string;
  /** Restrict to assemblies at a premise carrying an OPEN violation. */
  enforced?: boolean;
  /** Restrict to assemblies past their next test due date. */
  overdue?: boolean;
  /** Restrict to the ACTIVE non-compliant set per AWWA M14 §6.5 — terminal
   * (replaced / removed / abandoned) rows stay OFF the enforcement worklist. */
  non_compliant_only?: boolean;
  size_min?: number;
  size_max?: number;
  /** Inclusive lower bound on next_test_due_date, YYYY-MM-DD. */
  next_due_from?: string;
  /** Inclusive upper bound on next_test_due_date, YYYY-MM-DD. */
  next_due_to?: string;
  limit?: number;
  offset?: number;
  sort_by?:
    | "device_status"
    | "device_category"
    | "created_at"
    | "updated_at"
    | "next_test_due_date"
    | "last_test_completed"
    | "serial_number"
    | "size_inches"
    | "service_type"
    | "verdict"
    | "account"
    | "meter_number"
    | "meter_account"
    | "has_protective_enclosure"
    | "degree_of_hazard";
  sort_dir?: "asc" | "desc";
}

export const deviceInstallationKeys = {
  all: ["deviceInstallations"] as const,
  lists: () => [...deviceInstallationKeys.all, "list"] as const,
  list: (params: DeviceInstallationListParams) =>
    [...deviceInstallationKeys.lists(), params] as const,
  /**
   * Where a DISABLED list query parks. A disabled caller's params default to
   * the same `{limit, offset}` an enabled empty-params caller sends, so without
   * a key of its own the two share one cache entry — and removing the other
   * caller's guard would serve it rows it never asked for.
   */
  disabledList: () => [...deviceInstallationKeys.lists(), "disabled"] as const,
  details: () => [...deviceInstallationKeys.all, "detail"] as const,
  detail: (id: string) => [...deviceInstallationKeys.details(), id] as const,
  /** The detail stat strip's counts — shared by its reader and every writer that moves one. */
  relatedCounts: (id: string) => [...deviceInstallationKeys.all, "relatedCounts", id] as const,
} as const;

function withDefaults(params: DeviceInstallationListParams): DeviceInstallationListParams {
  return {
    ...params,
    limit: params.limit ?? 50,
    offset: params.offset ?? 0,
  };
}

/**
 * List device installation rows. Per AWWA M14 §6.5 the installation row is
 * the per-premise installation context (facility binding, plus an optional
 * meter link) for an inventory device of record. The identity
 * tuple (manufacturer / serial / size / listing track) lives on the joined
 * `DeviceInventory` row reached via `device_inventory_id`; the wider list
 * response carries a projected `inventory_summary` + `facility_summary`
 * pair per OpenAPI `DeviceInstallationListItem`.
 *
 * Pass `null` to disable the query — a caller whose filter is not resolved yet
 * would otherwise page the org's whole installation list.
 */
export function useDeviceInstallations(params: DeviceInstallationListParams | null = {}) {
  const enabled = params !== null;
  const merged = withDefaults(params ?? {});
  return useQuery<DeviceInstallationListResponse>({
    queryKey: enabled ? deviceInstallationKeys.list(merged) : deviceInstallationKeys.disabledList(),
    queryFn: async () => {
      const { data, error, response } = await apiClient.GET("/device-installations", {
        params: { query: merged },
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to list device installations";
        throw new ApiError(response.status, msg);
      }
      return data;
    },
    enabled,
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    placeholderData: keepPreviousData,
  });
}

/**
 * POST /device-installations mutation backing the single-step create form.
 * Per the installation-primary model the installation is the primary entity:
 * the common create body has NO `device_inventory_id` and NO `inventory_link`
 * (an inventory-less installation whose identity fields live directly on the
 * row). An asset link is OPTIONAL — when the operator opts in, the body
 * carries either a top-level `device_inventory_id` (link an existing asset)
 * or an `inventory_link` arm (`link_existing` / `register_new`); per the
 * api-core precedence rule `inventory_link` is authoritative over the
 * top-level id.
 *
 * Invalidation: always invalidate the installation lists. ONLY when the
 * created row links an inventory (the resolved inventory id from the response
 * `device_inventory_id`) do we also invalidate that inventory's detail + chain
 * keys — the inventory-less common case touches no inventory cache.
 *
 * The typed error envelope is threaded through `ApiError.details` so the route
 * can surface the 422 category-coherence / generic envelope on the form
 * banner.
 */
export function useCreateInstallation() {
  const queryClient = useQueryClient();
  return useMutation<DeviceInstallation, ApiError, DeviceInstallationCreateRequest>({
    mutationFn: async (body) => {
      const { data, error, response } = await apiClient.POST("/device-installations", { body });
      if (error) {
        const typed = error as { error?: string };
        const msg = typed.error ?? "Failed to create installation";
        throw new ApiError(response.status, msg, undefined, typed);
      }
      return data as DeviceInstallation;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: deviceInstallationKeys.lists() });
      // The created row only touches inventory cache when it actually links
      // an asset — the inventory-less common path leaves those keys alone.
      const inventoryId = data.device_inventory_id;
      if (inventoryId) {
        void queryClient.invalidateQueries({
          queryKey: deviceInventoryKeys.detail(inventoryId),
        });
        void queryClient.invalidateQueries({
          queryKey: installationChainKeys.detail(inventoryId),
        });
      }
    },
  });
}

/**
 * PATCH /device-installations/{id} mutation backing the Record tab's
 * inline field edits (`useInstallationInlineField`).
 * Per AWWA M14 §6 the EWAW + ENW field set on
 * `DeviceInstallationUpdate` is the operator-mutable surface; IUR /
 * INEE / RFO fields are absent from the schema and rejected with HTTP
 * 400 by kin-openapi `additionalProperties: false`. Terminal-status
 * rows (`replaced` / `removed` / `abandoned`) raise HTTP 409
 * `device_record_immutable_post_lifecycle_terminal` per AWWA M14 §6.5.
 *
 * Commit freshness: the PATCH echo is a REDUCED projection — verdict, the
 * manufacturer/model labels, the resolved account, and lineage members are
 * populated only by the detail-read projection — so the echo is NEVER written
 * into the detail cache. Instead onMutate optimistically merges the committed
 * body keys over the cached detail row (detail-only members ride the old
 * object untouched), and onError rolls the merge back and invalidates the
 * detail key: a refusal can mean the row moved under a stale page (a 409
 * `device_testing_paused` after an out-of-band pause), so the page refetches
 * the server's row instead of keeping the cached one. The lists key is left
 * alone on error — the merge never touched a list, and a refused PATCH
 * changed no row.
 *
 * On success: invalidates the detail key + the lists key, plus the joined
 * inventory detail key ONLY when the installation links an inventory
 * (`inventoryId` is null for the common inventory-less installation). The
 * detail refetch reconciles derived fields (the verdict derives from
 * `next_test_due_date`).
 */
export function usePatchDeviceInstallation(installationId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    DeviceInstallation,
    ApiError,
    { body: DeviceInstallationUpdate; inventoryId: string | null },
    { previous: DeviceInstallation | undefined }
  >({
    mutationFn: async ({ body }) => {
      const { data, error, response } = await apiClient.PATCH("/device-installations/{id}", {
        params: { path: { id: installationId } },
        body,
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to update device installation";
        throw new ApiError(response.status, msg, (error as { code?: string }).code);
      }
      return data;
    },
    onMutate: async ({ body }): Promise<{ previous: DeviceInstallation | undefined }> => {
      const detailKey = deviceInstallationKeys.detail(installationId);
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<DeviceInstallation>(detailKey);
      // device_status is coalesced so a body that carries the key undefined
      // keeps the row's status rather than spreading undefined over it.
      // The Update schema types grandfather_basis / non_testable_subtype
      // as plain `string | null` while the read schema narrows them to their
      // enums; the spread merge cannot satisfy both, so the optimistic write
      // narrows with a cast — the detail refetch in onSuccess reconciles the
      // canonical row immediately after.
      queryClient.setQueryData<DeviceInstallation>(detailKey, (old) =>
        old
          ? ({
              ...old,
              ...body,
              device_status: body.device_status ?? old.device_status,
            } as DeviceInstallation)
          : old,
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      const detailKey = deviceInstallationKeys.detail(installationId);
      if (context?.previous) {
        queryClient.setQueryData(detailKey, context.previous);
      }
      void queryClient.invalidateQueries({ queryKey: detailKey });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: deviceInstallationKeys.detail(installationId),
      });
      void queryClient.invalidateQueries({
        queryKey: deviceInstallationKeys.lists(),
      });
      if (vars.inventoryId) {
        void queryClient.invalidateQueries({
          queryKey: deviceInventoryKeys.detail(vars.inventoryId),
        });
      }
    },
  });
}

/**
 * Fetch a single installation row by id. Drives the detail page +
 * edit route-guard banner. Per AWWA M14 §6.5 the installation row is
 * the per-premise installation context; identity-track fields are on
 * the joined `device_inventory` row reached via `device_inventory_id`
 * and the detail page links into the inventory detail surface for
 * identity changes.
 */
export function useDeviceInstallationDetail(id: string) {
  return useQuery<DeviceInstallation>({
    queryKey: deviceInstallationKeys.detail(id),
    queryFn: async () => {
      const { data, error, response } = await apiClient.GET("/device-installations/{id}", {
        params: { path: { id } },
      });
      if (error) {
        const msg = (error as { error?: string }).error ?? "Failed to load device installation";
        throw new ApiError(response.status, msg);
      }
      return data;
    },
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    enabled: !!id,
  });
}
